<script lang="ts" setup>
import { inject, watch, toRef } from 'vue';
import type { PropType } from 'vue';

// TYPES
import type { ActionsStepGrid } from '@/types/ui/StepperTypes';

// UTILS
import { logger } from '@/shared/common/utils/logger.util';

// COMPONENTS
import { GButton } from '@flash-global66/g-button';
import gSteppersProvider, {
  initialGSteppersProvider,
} from '@/components/GStepper/config/gSteppersProvider';

const props = defineProps({
  maxWidth: {
    type: String,
    default: 'max-w-4xl',
  },
  isBigGrid: {
    type: Boolean,
    default: false,
  },
  messageSecure: {
    type: Boolean,
    default: true,
  },
  messageSecureText: {
    type: String,
    default: 'Sistema seguro',
  },
  title: {
    type: String,
    default: '',
  },
  subtitle: {
    type: String,
    default: '',
  },
  hideButtons: {
    type: Boolean,
    default: false,
  },
  contentAlign: {
    type: String,
    default: 'justify-between',
  },
  heightFull: {
    type: Boolean,
    default: false,
  },
  buttonsAlignment: {
    type: String,
    default: 'flex-col',
  },
  overflowInViewport: {
    type: Boolean,
    default: false,
  },
  contentClass: {
    type: String,
    default: '',
  },
  actions: {
    type: Object as PropType<ActionsStepGrid>,
    default: (): ActionsStepGrid => ({
      next: {
        label: 'Continuar',
        function: () => logger.debug('GStepGrid: acción sin handler', { action: 'next' }),
        hide: false,
        disabled: false,
      },
      back: {
        label: 'Volver',
        function: () => logger.debug('GStepGrid: acción sin handler', { action: 'back' }),
        hide: false,
        disabled: false,
      },
      buttonBack: {
        label: 'Volver',
        function: () => logger.debug('GStepGrid: acción sin handler', { action: 'buttonBack' }),
        hide: false,
        disabled: false,
      },
      optional: {
        label: 'Opcional',
        function: () => logger.debug('GStepGrid: acción sin handler', { action: 'optional' }),
        hide: false,
        disabled: false,
        variant: 'tertiary',
      },
    }),
  },
});
const { gStepperMethods } = inject(gSteppersProvider, initialGSteppersProvider);
const actions = toRef(props, 'actions');

const hideBackButton = () => {
  if (typeof actions.value?.back === 'undefined') {
    return true;
  } else if (typeof actions.value.back?.hide === 'boolean') {
    return actions.value.back?.hide;
  } else {
    return false;
  }
};

const disabledBackButton = () => {
  if (typeof actions.value?.back === 'undefined') {
    return true;
  } else if (typeof actions.value.back?.disabled === 'boolean') {
    return actions.value.back?.disabled;
  } else {
    return false;
  }
};

watch(
  actions,
  (newActions) => {
    gStepperMethods.navigateBack(
      newActions.back?.function ||
        (() => {
          logger.debug('GStepGrid: acción sin handler', {
            action: 'back',
            title: props.title,
          });
        }),

      {
        hide: props.hideButtons ? true : hideBackButton(),
        disabled: disabledBackButton(),
      }
    );
  },
  { immediate: true }
);
</script>

<template>
  <div
    class="w-full h-full overflow-auto flex flex-col"
    :class="[contentAlign]"
  >
    <div
      :class="[
        maxWidth,
        contentClass,
        overflowInViewport
          ? 'h-full flex flex-col overflow-y-auto scrollbar-custom'
          : '',
      ]"
      class="px-lg lg:px-none w-full mx-auto"
    >
      <slot name="title">
        <h2
          v-if="title"
          class="text-center pb-lg text-6 md:text-8 text-primary-txt font-semibold leading-tight"
          :class="contentClass ? '' : 'mt-2xl'"
          v-html="title"
        />
      </slot>

      <slot name="subtitle">
        <div
          v-if="subtitle"
          class="flex flex-col md:items-center md:text-center"
        >
          <h3
            v-if="subtitle"
            class="text-3 md:text-5 text-secondary-txt md:font-medium mb-xl"
            v-html="subtitle"
          />
        </div>
      </slot>

      <div class="w-full" :class="{ 'h-full': !title || heightFull }">
        <slot name="content" />
      </div>
    </div>

    <div>
      <div
        v-if="!hideButtons"
        class="w-full pt-xs px-lg lg:px-none"
        :class="{ 'pb-md': !messageSecure }"
      >
        <slot name="actions">
          <div
            class="flex flex-col sm:flex-row justify-center items-center pt-lg"
          >
            <div class="flex gap-md" :class="[buttonsAlignment]">
              <g-button
                v-if="actions.next && !actions.next.hide"
                class="sm:order-3 w-96 max-w-full"
                :disabled="actions.next.disabled"
                :loading="actions.next?.loading"
                @click="actions?.next?.function()"
                :data-test="`button:${actions.next.label}`"
              >
                {{ actions.next.label }}
              </g-button>
              <g-button
                v-if="actions.buttonBack && !actions.buttonBack.hide"
                class="sm:order-3 w-96 max-w-full"
                :disabled="actions.buttonBack.disabled"
                :loading="actions.buttonBack?.loading"
                @click="actions?.buttonBack?.function()"
                variant="tertiary"
                :data-test="`button:${actions.buttonBack.label}`"
              >
                {{ actions.buttonBack.label }}
              </g-button>
            </div>
            <g-button
              v-if="actions.optional && !actions.optional.hide"
              :variant="actions.optional.variant || 'tertiary'"
              class="mt-xs ml-xs sm:mt-none sm:mr-sm w-64"
              :disabled="actions.optional.disabled"
              :loading="actions.optional?.loading"
              @click="actions?.optional?.function"
              :data-test="`button:${actions.optional.label}`"
            >
              {{ actions.optional.label }}
            </g-button>
          </div>
        </slot>
      </div>

      <div
        v-if="messageSecure"
        class="flex justify-center items-center text-terciary-txt text-2 my-md mx-auto"
      >
        <slot name="secure-message">
          <fa-icon class="mr-xs" :icon="['fas', 'lock-alt']" />
          <p v-html="messageSecureText"></p>
        </slot>
      </div>
    </div>
  </div>
</template>
